import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  Users, 
  Settings, 
  Car,
  Hotel,
  Scissors,
  Stethoscope,
  Dumbbell,
  Utensils,
  CheckCircle2,
  ArrowRight,
  Link as LinkIcon
} from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Calendar,
    title: 'Sveigjanleg bókunarkerfi',
    description: 'Sérsniðin bókunarkerfi fyrir allar atvinnugreinar.'
  },
  {
    icon: Clock,
    title: 'Sjálfvirk tímastjórnun',
    description: 'Sjálfvirk stýring á tímabókun og staðfestingum.'
  },
  {
    icon: Users,
    title: 'Notendavænt viðmót',
    description: 'Einfalt og þægilegt viðmót fyrir viðskiptavini.'
  },
  {
    icon: Settings,
    title: 'Sérsniðin virkni',
    description: 'Aðlagað að þínum þörfum og rekstri.'
  },
  {
    icon: CheckCircle2,
    title: 'Sjálfvirkar áminningar',
    description: 'SMS og tölvupóstáminningar fyrir bókanir.'
  },
  {
    icon: LinkIcon,
    title: 'Tengingar við önnur kerfi',
    description: 'Samþætting við bókhald, CRM og önnur kerfi.'
  }
];

const industries = [
  {
    icon: Car,
    title: 'Bílaleigur',
    description: 'Sérhannað bókunarkerfi fyrir bílaleigur',
    features: [
      'Bílastöðuyfirlit í rauntíma',
      'Sjálfvirk verðlagning',
      'Tryggingasamþætting',
      'Afhending og skil',
      'Viðhaldsáætlun',
      'Greiðslugáttir',
      'Tengingar við bókhald'
    ]
  },
  {
    icon: Hotel,
    title: 'Hótel og gistihús',
    description: 'Alhliða bókunarkerfi fyrir gistirými',
    features: [
      'Herbergjayfirlit',
      'Fjölbreyttar bókanir',
      'Channel Manager',
      'Innritun/útritun',
      'Þrif og viðhald',
      'Minibar og aukaþjónusta',
      'Gestaskráning'
    ]
  },
  {
    icon: Scissors,
    title: 'Hárgreiðslustofur',
    description: 'Bókunarkerfi fyrir hárgreiðslustofur',
    features: [
      'Starfsmannabókanir',
      'Þjónustuyfirlit',
      'Viðskiptavinasaga',
      'SMS áminningar',
      'Biðlisti',
      'Afslættir og tilboð',
      'Vörusala'
    ]
  },
  {
    icon: Stethoscope,
    title: 'Heilbrigðisþjónusta',
    description: 'Öruggt bókunarkerfi fyrir heilbrigðisþjónustu',
    features: [
      'Læknabókanir',
      'Sjúkraskrártenging',
      'Örugg samskipti',
      'Sjúkratryggingar',
      'Lyfseðlaendurnýjun',
      'Fjarviðtöl',
      'GDPR samræmi'
    ]
  },
  {
    icon: Dumbbell,
    title: 'Líkamsræktarstöðvar',
    description: 'Bókunarkerfi fyrir líkamsrækt og námskeið',
    features: [
      'Hóptímar og námskeið',
      'Einkaþjálfun',
      'Aðgangsstýring',
      'Áskriftarstjórnun',
      'App tenging',
      'Mætingaskráning',
      'Biðlisti'
    ]
  },
  {
    icon: Utensils,
    title: 'Veitingastaðir',
    description: 'Bókunarkerfi fyrir veitingastaði',
    features: [
      'Borðapantanir',
      'Borðauppröðun',
      'Séróskir',
      'Hópa- og veislupantanir',
      'Biðlisti',
      'Take-away pantanir',
      'Matseðilstenging'
    ]
  }
];

const Booking = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Sérsniðin bókunarkerfi fyrir þitt fyrirtæki
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Við bjóðum upp á sérhönnuð bókunarkerfi sem eru sérsniðin að þörfum 
              mismunandi atvinnugreina.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá tilboð
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Allt sem þú þarft í bókunarkerfi
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á heildarlausn fyrir bókunarkerfi
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Industries Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Sérsniðnar lausnir fyrir þína atvinnugrein
            </h2>
            <p className="text-xl text-gray-600">
              Bókunarkerfi sérhönnuð fyrir mismunandi þarfir
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <industry.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{industry.title}</h3>
                <p className="text-gray-600 mb-6">{industry.description}</p>
                <ul className="space-y-3 mb-8">
                  {industry.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-[#EA295A] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#d01d4c] transition-all"
                  >
                    Fá tilboð
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að einfalda bókunarferlið?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að setja upp bókunarkerfi sem virkar fyrir 
              þitt fyrirtæki
            </motion.p>
            <motion.div>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Fá tilboð
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Booking;